import {
  HABIT_ICONS,
  HABIT_ICON_GROUPS,
  guessHabitIcon,
} from '@goaltree/backend/convex/lib/habitIcons'
import { cn } from '@goaltree/ui/lib/utils'
import {
  Apple,
  Baby,
  BedDouble,
  Bike,
  BookOpen,
  Brain,
  Briefcase,
  Calendar,
  Camera,
  Carrot,
  Cat,
  CigaretteOff,
  Clock,
  Code,
  Coffee,
  Coins,
  Dog,
  Droplet,
  Dumbbell,
  Ear,
  Flame,
  Flower2,
  Footprints,
  Gamepad2,
  Gift,
  GraduationCap,
  Guitar,
  Hammer,
  HandHeart,
  Heart,
  HeartPulse,
  Hourglass,
  House,
  Languages,
  Laptop,
  Leaf,
  Lightbulb,
  ListChecks,
  type LucideIcon,
  Mail,
  MessageCircle,
  Mic,
  Moon,
  Mountain,
  Music,
  Palette,
  PenLine,
  Pencil,
  PersonStanding,
  Phone,
  PhoneOff,
  PiggyBank,
  Pill,
  Plane,
  Receipt,
  Salad,
  Shirt,
  ShowerHead,
  Smile,
  Snowflake,
  Sparkles,
  Sprout,
  Star,
  Sun,
  Target,
  Timer,
  Trash2,
  TrendingUp,
  Trophy,
  Users,
  Utensils,
  Wallet,
  Waves,
  Wind,
  WineOff,
  Zap,
} from 'lucide-react'
import { type CSSProperties, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { habitColor } from '@/components/inbox-habits'
import { useCategoryFill } from '@/lib/category-color'
import { POPULAR_HABITS } from '@/lib/popular-habits'

const ICONS: Record<string, LucideIcon> = {
  apple: Apple,
  baby: Baby,
  'bed-double': BedDouble,
  bike: Bike,
  'book-open': BookOpen,
  brain: Brain,
  briefcase: Briefcase,
  calendar: Calendar,
  camera: Camera,
  carrot: Carrot,
  cat: Cat,
  'cigarette-off': CigaretteOff,
  clock: Clock,
  code: Code,
  coffee: Coffee,
  coins: Coins,
  dog: Dog,
  droplet: Droplet,
  dumbbell: Dumbbell,
  ear: Ear,
  flame: Flame,
  'flower-2': Flower2,
  footprints: Footprints,
  'gamepad-2': Gamepad2,
  gift: Gift,
  'graduation-cap': GraduationCap,
  guitar: Guitar,
  hammer: Hammer,
  'hand-heart': HandHeart,
  heart: Heart,
  'heart-pulse': HeartPulse,
  hourglass: Hourglass,
  house: House,
  languages: Languages,
  laptop: Laptop,
  leaf: Leaf,
  lightbulb: Lightbulb,
  'list-checks': ListChecks,
  mail: Mail,
  'message-circle': MessageCircle,
  mic: Mic,
  moon: Moon,
  mountain: Mountain,
  music: Music,
  palette: Palette,
  'pen-line': PenLine,
  pencil: Pencil,
  'person-standing': PersonStanding,
  phone: Phone,
  'phone-off': PhoneOff,
  'piggy-bank': PiggyBank,
  pill: Pill,
  plane: Plane,
  receipt: Receipt,
  salad: Salad,
  shirt: Shirt,
  'shower-head': ShowerHead,
  smile: Smile,
  snowflake: Snowflake,
  sparkles: Sparkles,
  sprout: Sprout,
  star: Star,
  sun: Sun,
  target: Target,
  timer: Timer,
  'trash-2': Trash2,
  'trending-up': TrendingUp,
  trophy: Trophy,
  users: Users,
  utensils: Utensils,
  wallet: Wallet,
  waves: Waves,
  wind: Wind,
  'wine-off': WineOff,
  zap: Zap,
}

type HabitLike = { icon?: string | null; title: string; category?: string | null }

// Saved icon → the popular habit with this (localised) title → keyword guess,
// so suggested habits get the right icon in any language.
export const habitIconComponent = (name: string): LucideIcon => ICONS[name] ?? Target

export function useHabitIconName(habit: HabitLike): string {
  const { t, i18n } = useTranslation()
  const popular = useMemo(
    () =>
      new Map(POPULAR_HABITS.map((h) => [t(`inbox.popularHabits.${h.key}`).toLowerCase(), h.icon])),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rebuilt per language
    [i18n.language],
  )
  if (habit.icon && HABIT_ICONS.includes(habit.icon)) return habit.icon
  const title = habit.title.split('///')[0].trim().toLowerCase()
  return popular.get(title) ?? guessHabitIcon(title, habit.category)
}

// A habit's icon on a soft tile of its colour (HabitKit-style).
export function HabitIcon({
  habit,
  color,
  size = 'sm',
  className,
}: {
  habit: HabitLike
  color?: string
  size?: 'sm' | 'md'
  className?: string
}) {
  const fill = useCategoryFill()
  const Icon = ICONS[useHabitIconName(habit)] ?? Target
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex shrink-0 items-center justify-center bg-[color-mix(in_srgb,var(--habit)_16%,transparent)] text-[var(--habit)]',
        size === 'sm' ? 'size-5 rounded-[5px]' : 'size-9 rounded-lg',
        className,
      )}
      style={{ '--habit': color ?? fill(habitColor(habit.category ?? undefined)) } as CSSProperties}
    >
      <Icon className={size === 'sm' ? 'size-3' : 'size-5'} strokeWidth={2.4} />
    </span>
  )
}

export function HabitIconPicker({
  value,
  color,
  onPick,
}: {
  value: string
  color: string
  onPick: (icon: string | undefined) => void
}) {
  const { t } = useTranslation()
  return (
    <div
      className="flex max-h-72 flex-col gap-3 overflow-y-auto p-2"
      style={{ '--habit': color } as CSSProperties}
    >
      {HABIT_ICON_GROUPS.map((g) => (
        <div key={g.key} className="flex flex-col gap-1.5">
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
            {t(`inbox.iconGroup.${g.key}`)}
          </p>
          <div className="grid grid-cols-6 gap-1">
            {g.icons.map((name) => {
              const Icon = ICONS[name] ?? Target
              const active = name === value
              return (
                <button
                  key={name}
                  type="button"
                  aria-label={name}
                  aria-pressed={active}
                  onClick={() => onPick(name)}
                  className={cn(
                    'flex aspect-square cursor-pointer items-center justify-center rounded-md transition-colors',
                    active
                      ? 'bg-[color-mix(in_srgb,var(--habit)_20%,transparent)] text-[var(--habit)] ring-1 ring-[var(--habit)]'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  <Icon className="size-4" strokeWidth={2.2} />
                </button>
              )
            })}
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onPick(undefined)}
        className="cursor-pointer rounded-md py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        {t('inbox.iconAuto')}
      </button>
    </div>
  )
}
